<script setup lang="ts">
import { storeToRefs } from 'pinia';

import { Button } from '@clovyr/bed';
import { AppLogo, type Logo } from '@clovyr/bed';
import markdown from '@clovyr/bed/components/blocks/Markdown.vue';
import Tooltip from '@clovyr/bed/components/blocks/Tooltip.vue';
import { ButtonShape } from '@clovyr/bed/components/controls/types';
import Copy from '@clovyr/bed/components/elements/Copy.vue';
import { WNextDeployment } from '@clovyr/pollen/deployment/wnext';
import { getPrimaryDNS } from '@clovyr/pollen/manifest';
import { replaceVariables } from '@clovyr/pollen/manifest/util';

import Pin from '@/components/controls/Pin.vue';
import Trash from '@/components/controls/Trash.vue';
import Module from '@/components/elements/Module.vue';
import Spinner from '@/components/elements/Spinner.vue';
import ArrowBackIcon from '@/components/icons/ArrowBackIcon.vue';
import EditIcon from '@/components/icons/EditIcon.vue';
import ShareIcon from '@/components/icons/ShareIcon.vue';
import { useAppHead } from '@/composables/useAppHead';
import { useAppResume } from '@/composables/useAppResume';
import { useAppVersion } from '@/composables/useAppVersion';
import { usePayments } from '@/composables/usePayments';
import { useRemoteExec } from '@/composables/useRemoteExec';
import { useEventBus } from '@/stores/event_bus';
import { useLibraryFlashStore } from '@/stores/library_flash';
import { usePollenStore } from '@/stores/pollen_store';
import { FeatureFlag, useUserFlagsStore } from '@/stores/user_flags';

import Modal from '../../components/elements/Modal.vue';
import { useAppStatus } from '../../composables/useAppStatus';

import AICloudAccess from './AICloudAccess.vue';
import AppConfig from './AppConfig.vue';
import AppEssentials from './AppEssentials.vue';
import AppMailProxy from './AppMailProxy.vue';
import AppStatus from './AppStatus.vue';
import DeleteApp from './DeleteApp.vue';
import RenameApp from './RenameApp.vue';
import ScriptForm from './ScriptForm.vue';
import Sharing from './Sharing.vue';
import TrialStatus from './TrialStatus.vue';
import Troubleshooting from './Troubleshooting.vue';
import Upgrade from './Upgrade.vue';
import VersionsBackups from './VersionsBackups.vue';
import Welcome from './Welcome.vue';

// large component chain, make async
const InternalTools = defineAsyncComponent(() => import('./InternalTools.vue'));

const props = defineProps<{
  id: string;
  sharedDeploymentID?: string;
}>();

const payments = usePayments();
const pollenStore = usePollenStore();
const userFlagStore = useUserFlagsStore();

const { canAccessInternalOnlyFeatures, canAccessPreviewFeatures } = storeToRefs(userFlagStore);

const libraryDeployment = computed(() =>
  pollenStore.getDeploymentByLibraryRoute(
    props.sharedDeploymentID ?? props.id,
    props.sharedDeploymentID ? props.id : undefined,
  ),
);
// Keep the resolved identity when passing the deployment to child modules and composables.
// The friendly deployment ID can be shared by multiple library items.
const deploymentID = computed(() => libraryDeployment.value?.libraryItemID ?? props.id);
const libraryFlash = useLibraryFlashStore();
const statusMessage = ref('');
watch(
  deploymentID,
  (id) => {
    statusMessage.value = libraryFlash.take(id);
  },
  { immediate: true },
);
const {
  deployment,
  app,
  sub,
  isAppSubscribed,
  refreshSubItem,
  isClovyrCode,
  isRunning,
  isSleeping,
  subItem,
} = useAppStatus(deploymentID);

provide('deploymentID', deploymentID);
provide('deployment', deployment);
provide('app', app);

const { isUserAuthenticated } = storeToRefs(pollenStore);
const { eventBus } = useEventBus();
const { refreshSubscription } = usePayments();
const router = useRouter();

const gettingStarted = computed(() => {
  if (!(app.value?.metadata?.gettingStarted && deployment.value)) {
    return '';
  }

  return replaceVariables(app.value.metadata.gettingStarted, deployment.value.appSettings);
});

const showDeleteModal = ref(false);
const showRenameModal = ref(false);

const hasMailHog = computed(() => {
  return !!app.value.deployment?.addons?.mailhog;
});

const onClickRenameApp = () => {
  showRenameModal.value = true;
};

const onClickDeleteApp = async () => {
  showDeleteModal.value = true;
};

const canResume = computed(() => {
  return isSleeping.value && !isClovyrCode.value && isAppSubscribed.value;
});

const resuming = ref(false);
const onClickResume = async () => {
  resuming.value = true;
  try {
    await useAppResume(deploymentID).wakeUpInstance();
  } finally {
    resuming.value = false;
  }
};

const logo = computed((): Logo => {
  return {
    logoUrl: app.value.metadata?.logoUrl,
    logoBackgroundColor: app.value.metadata?.logoBackgroundColor,
  };
});

/**
 * Link to the application when clicking the 'open' button.
 *
 * For code environments its san internal URL with the 'view' iframe.
 * For all others, opens the external URL to the server.
 *
 */
const appLink = computed(() => {
  if (isClovyrCode.value) {
    // TODO: fix the link between 'app' & clovyr-code. we know in PublisherApp if it's code or not,
    // perhaps we use that link here? or a new field in the app manifest?
    return `/view/${deployment.value!.instanceName}`;
  }
  let host = deployment.value.customDomain || deployment.value.fqdn;
  const d = getPrimaryDNS(app.value);
  if (d) {
    host = `${d[0]}-${host}`;
  }
  return `https://${host}`;
});

async function onClickOpen() {
  void new WNextDeployment().keepalive(deployment.value.id, deployment.value.fqdn!);
}

// const onClickMail = () => {
//   void router.push({ name: 'LibraryAppMail', params: { id: deployment.value.id } });
// };

async function mounted() {
  useAppHead(app.value.metadata, deployment.value.instanceName);
  sub.value = await payments.getSubscription();
  await refreshSubItem();
}

onMounted(async () => {
  void mounted();

  if (isRunning.value) {
    void useAppVersion(deploymentID).runPostLaunchTasks();
  }
});

function handleTogglePin() {
  if (!deployment.value) {
    return;
  }

  deployment.value.isPinned = !deployment.value.isPinned;
  void pollenStore.pollen.putDeployment(deployment.value);
}

const CLOVYR_AI_UPGRADE_CUTOFF = 1766179894116; // Dec 19, 2025

const showUpgradeWidget = computed(() => {
  return deployment.value.appID === 'clovyr-ai-cloud' || canAccessPreviewFeatures.value;
});

onMounted(async () => {
  if (
    isRunning.value &&
    deployment.value?.appID === 'anything-llm' &&
    deployment.value.createdAt < CLOVYR_AI_UPGRADE_CUTOFF
  ) {
    // migrate to clovyr-ai-cloud
    const { execWithOutput } = useRemoteExec(deployment.value);
    await execWithOutput(`cd /opt/clovyr/apps && ln -s anything-llm clovyr-ai-cloud`);
    const id = 'clovyr-ai-cloud';
    const name = 'Clovyr AI Cloud';
    const d = toRaw(deployment.value);
    d.appID = id;
    d.appName = name;
    d.id = id;
    d.instanceName = name;
    await pollenStore.pollen.putDeployment(d);
    const params = pollenStore.getLibraryRouteParams(d);
    void router.replace({
      name: 'libraryItemPrefix' in params ? 'LibraryAppDetailQualified' : 'LibraryAppDetail',
      params,
    });
  }
});

const hasAppConfigFlag = computed(() => {
  return userFlagStore.userHasFeatureFlag(FeatureFlag.AppConfig);
});
const isReadOnly = computed(() => {
  return deployment.value.accessPolicy === 'read_only';
});
const canShare = computed(() => {
  return deployment.value.accessPolicy === undefined || deployment.value.accessPolicy === 'owner';
});

const { pollen } = usePollenStore();

async function getSharingLink(policy: 'editor' | 'read_only') {
  const link = encodeURI(await pollen.shareDeployment(deploymentID.value, policy));
  await navigator.clipboard.writeText(link);
  return link;
}

const showUnfollowModal = ref(false);

const handleUnfollow = async () => {
  await pollenStore.deleteDeployment(deployment.value);
  showUnfollowModal.value = false;
  await router.push({ name: 'Library' });
};

const onClickShowPaymentModal = async (): Promise<void> => {
  // if user not authed, show log in modal
  // TODO: Chetan wants to revisit this logic

  // if (!isUserAuthenticated.value && authState.value === AuthState.LoggedOut) {
  //   eventBus.emit('modal:login:show', {
  //     form: ShowAuthForm.Login,
  //     opts: {
  //       hed: 'Please log in to upgrade hosting.',
  //       modal: true,
  //     },
  //   });
  // }

  if (!sub.value || !subItem.value) {
    await refreshSubscription();
    if (!sub.value) {
      throw new Error('subscription not found');
    }
    if (!subItem.value) {
      throw new Error('subscription item not found');
    }
  }
  eventBus.emit('modal:payment:show', {
    id: deploymentID.value,
    name: deployment.value.appName,
    sub: sub.value,
    subItem: subItem.value,
  });
};

const script = computed(() => {
  return app.value?.scripts?.create_user;
});
</script>

<template>
  <div
    v-if="deployment && app"
    class="app-instance"
    :class="{
      'app--shared': isReadOnly,
    }"
  >
    <div class="app-instance__top">
      <div class="app-background"></div>

      <!-- Second column (first here so it flows correctly when wrapping) -->
      <div class="container">
        <div class="row">
          <div class="grid__col">
            <router-link to="/library" class="app-instance__back text text--mono">
              <ArrowBackIcon />
              Back to App Library
            </router-link>
          </div>
        </div>

        <div class="app-instance__hero">
          <div class="app-instance__hero__grid grid">
            <div class="app-instance__hero__logo">
              <AppLogo size="large" :logo="logo" />
            </div>

            <div class="app-instance__hero__title grid__col">
              <div
                v-if="!isReadOnly"
                class="app-header__preheading text text--color-grey-secondary"
              >
                {{ deployment.appName }}
              </div>
              <div v-if="isReadOnly" class="app-header__preheading">
                {{ deployment.appName }} is shared with you
              </div>

              <div class="app-header__heading-container">
                <h2 class="app-header__heading">{{ deployment.instanceName }}</h2>

                <div v-if="!isReadOnly" class="app-edit-icon">
                  <EditIcon @click="onClickRenameApp" />
                </div>
              </div>
            </div>

            <!-- Action toolbar/buttons -->
            <div class="app-instance__hero__buttons grid__col">
              <Button v-if="isReadOnly" @click="showUnfollowModal = true" label="unfollow" />
              <Pin v-if="!isReadOnly" @click="handleTogglePin" :pinned="deployment.isPinned"></Pin>
              <Button v-if="!isReadOnly" @click="onClickDeleteApp" :shape="ButtonShape.Circle">
                <Trash />
              </Button>

              <!-- TODO: Uncomment when ready to use share modal -->
              <!-- <Copy
                :icon="ShareIcon"
                hasModal
                :translucent="false"
                @openModal="handleShowShareModal"
                :disabled="!isRunning || !isUserAuthenticated || isShared"
              /> -->
              <Tooltip
                v-if="canShare"
                class="share-tooltip"
                placement="bottom"
                theme="narrow"
                arrow
                content="share link"
              >
                <Copy
                  :icon="ShareIcon"
                  :text="() => getSharingLink('editor')"
                  :translucent="false"
                  :disabled="!isUserAuthenticated"
                />
              </Tooltip>
              <Button
                v-if="canResume && !isReadOnly"
                class="resume"
                label="resume"
                :reversed="true"
                @click="onClickResume"
                :disabled="resuming"
              />
              <Button
                v-else
                class="open"
                label="Open"
                :reversed="true"
                :link="appLink"
                link-target="_blank"
                @click="onClickOpen"
                :disabled="!isRunning && !isClovyrCode"
              />
            </div>
          </div>
        </div>
        <AppStatus v-if="!isClovyrCode && !isReadOnly" :id="deploymentID" />
        <p v-if="statusMessage" class="app-invite-status" role="status">{{ statusMessage }}</p>
      </div>
    </div>

    <!-- First column -->
    <div v-if="!isReadOnly" class="container">
      <div class="row">
        <div class="grid__col-md-12">
          <AppMailProxy v-if="hasMailHog" :id="deploymentID" />
        </div>
      </div>
      <div class="app-instance__twoup-modules row">
        <div class="app-instance__twoup-modules__left grid__col-md-6">
          <TrialStatus :id="deploymentID" @upgrade="onClickShowPaymentModal" />

          <Welcome v-if="script" show-close module-id="welcome" />

          <!-- module: app essentials -->
          <AppEssentials :id="deploymentID" />

          <AICloudAccess
            v-if="deployment.appID === 'clovyr-ai-cloud' && !isReadOnly"
            :app="app"
            :deployment="deployment"
          />

          <Troubleshooting v-if="!isClovyrCode" :id="deploymentID" />

          <!-- TODO: Uncomment when ready to use share modal -->
          <!-- <Sharing
            :id="deploymentID"
            v-if="canAccessPreviewFeatures && !isShared"
            @show-share-modal="handleShowShareModal"
          /> -->
          <Sharing
            v-if="isUserAuthenticated"
            :id="deploymentID"
            :editor-link="() => getSharingLink('editor')"
            :read-only-link="() => getSharingLink('read_only')"
          />
        </div>

        <div class="app-instance__twoup-modules__right grid__col-md-6">
          <!-- module: app access -->
          <!-- show this only if there is more than one url available? -->
          <!-- <Module class="module--appaccess" hed="App Access">
            <div class="module--stat">
              <div class="module__stat__label">LND Connect:</div>
              <div class="module__stat__value module__stat__value--mono module__stat__value--link">
                <a>
                  https://rpc-url-isverylong/d5678
                  <ArrowForwardsIcon />
                </a>
              </div>
            </div>
          </Module> -->

          <!--Module: Getting Started-->
          <Module
            v-if="gettingStarted"
            :bigHead="true"
            :hed="'Getting Started'"
            class="getting-started"
            ><!--v-if="showGettingStarted"-->
            <template #default>
              <div class="row">
                <div class="grid__col">
                  <div class="module__blurb">
                    <markdown :content="gettingStarted" />
                  </div>
                </div>
              </div>
            </template>
          </Module>

          <ScriptForm v-if="script" :script="script" />
          <!-- show welcome here if no scripts -->
          <Welcome v-else show-close module-id="welcome" />

          <!--Module: Versions-->
          <!-- TODO: Uncomment with app updates -->
          <div class="row versions" v-if="showUpgradeWidget">
            <div class="grid__col">
              <Upgrade :id="deploymentID" />
            </div>
          </div>

          <!--Module: Versions & Backups-->
          <div class="row versions" v-if="canAccessInternalOnlyFeatures">
            <div class="grid__col">
              <VersionsBackups :id="deploymentID" />
            </div>
          </div>

          <!--Module: Internal Tools -->
          <div class="row versions" v-if="canAccessInternalOnlyFeatures">
            <div class="grid__col">
              <InternalTools :id="deploymentID" />
            </div>
          </div>

          <div class="row" v-if="canAccessPreviewFeatures || hasAppConfigFlag">
            <div class="grid__col">
              <AppConfig :id="deploymentID" />
            </div>
          </div>

          <!--Module:Event Log-->
          <Module v-if="false" :bigHead="true" :hed="'Event Log'" class="event-log">
            <template #default>
              <div class="gradient"></div>
              <div class="log-text"></div>
            </template>
          </Module>
        </div>
      </div>
    </div>
  </div>

  <div v-else>
    <Spinner></Spinner>
  </div>

  <Modal v-if="showUnfollowModal" size="medium" hide-close>
    <template #body>
      <div class="unfollow-modal--text">
        <h2>Are you sure you want to unfollow {{ app.metadata.name }}</h2>
        <h4>You will no longer be able to access this app once you unfollow.</h4>
      </div>
    </template>
    <template #controls-right>
      <Button label="cancel" @click="showUnfollowModal = false" />
      <Button label="unfollow" reversed @click="handleUnfollow" />
    </template>
  </Modal>

  <DeleteApp
    v-if="showDeleteModal"
    :app="app"
    :deployment="deployment"
    @close="showDeleteModal = false"
  />

  <RenameApp
    v-if="showRenameModal"
    :app="app"
    :deployment="deployment"
    @close="showRenameModal = false"
  />

  <router-view></router-view>
</template>

<style scoped lang="scss">
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
@use 'styles/core/colors';
@use 'styles/core/layout/grid-vars';
@use 'styles/vendor/bootstrap/scss/mixins/breakpoints';

.app-invite-status {
  margin: 1rem 0;
  padding: 1rem 1.5rem;
  border: 1px solid var(--collection-text-translucent);
  border-radius: 0.5rem;
  background: var(--30-midnight, rgba(34, 26, 51, 0.3));
}

.getting-started {
  :deep(.module__head) {
    background-color: rgba(132, 115, 152, 0.1);
  }
  :deep(.module__body) {
    color: #e0dde4;
  }
}

.welcome {
  .hosting-heading {
    &__button {
      display: flex;
      justify-content: flex-end;

      :deep(.plain-button) {
        display: flex;
      }
    }
  }
  :deep(.module__head) {
    background-color: rgba(132, 115, 152, 0.1);
  }
  :deep(.module__head__grid) {
    display: flex;
    flex-direction: row;

    @include breakpoints.media-breakpoint-only(md) {
      flex-direction: column;
      align-items: center;
    }

    .module__head__right {
      margin-top: 1rem;
    }
  }

  :deep(.module__head__left) {
    .module__head__sub {
      margin-bottom: 1rem;
    }
  }
  :deep(.module__head__right) {
    flex: none;
    width: grid-vars.space(15);
    padding-right: 0;
    padding-left: 0;
  }

  :deep(.module__body) {
    color: #e0dde4;
    padding-top: grid-vars.space(3);

    .section-1 {
      margin-left: grid-vars.space(1);
      margin-right: grid-vars.space(1);
      margin-bottom: grid-vars.space(3);
      font-size: 16px;

      .module__blurb__body {
        margin-bottom: grid-vars.space(2);
      }
    }
  }
  :deep(.default2) {
    color: #e0dde4;
    background: var(--30-midnight, rgba(34, 26, 51, 0.3));

    .row {
      display: flex;
      flex-direction: column;

      @include breakpoints.media-breakpoint-up(lg) {
        flex-direction: row;
      }

      > * {
        margin-top: 1rem;
      }

      .module__blurb__head svg {
        @include breakpoints.media-breakpoint-down(sm) {
          margin-left: unset;
        }
      }
    }

    .links {
      color: #ada6b7;
      font-size: 16px;
      margin-bottom: grid-vars.space(3);
    }
  }
}
.event-log {
  :deep(.module__head) {
    padding-bottom: grid-vars.space(1);
  }

  :deep(.module__body) {
    color: #ada6b7;
    background-color: #1a122d;
    height: grid-vars.space(20);
    overflow-y: scroll;

    // .gradient {
    //   background: linear-gradient(180deg, #221A33 0%, rgba(34, 26, 51, 0.00) 100%);
    //   height: 20px;
    // }
    .log-text {
      color: #ada6b7;
      font-size: 12px;
      font-family: IBM Plex Mono;
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
    }
  }
  :deep(.module__head) {
    background-color: #1a122d;
    padding-top: grid-vars.space(3);
  }

  // ::-webkit-scrollbar {
  //   width: 5px;
  //   border-radius: 4px;
  // }
}

.app-instance__hero__buttons {
  height: grid-vars.space(5);
  justify-content: flex-end;

  a {
    margin-top: grid-vars.space(0.2);
    width: fit-content;
  }

  button {
    margin-top: grid-vars.space(0.3);
  }
}

.app--shared {
  .app-background::after {
    background: linear-gradient(180deg, rgba(132, 91, 57, 0.629) 50%, rgb(34 26 51 / 60%) 100%);
    backdrop-filter: blur(10px);
  }
  .app-header__preheading {
    color: colors.color(yellow, primary);
  }
  .open {
    background-color: colors.color(collection-2, primary);
  }
  :deep(.unpinned-icon) {
    circle {
      stroke: rgb(203, 189, 189);
    }
    path {
      fill: #cbbddb;
      stroke: #cbbddb;
    }
  }
}

.share-tooltip {
  margin-right: 0.75rem;
}

//Share modal styles?
.share-btn {
  margin-right: 1rem;
  :deep(.check-icon) {
    margin-left: 0.25rem;
    height: 1rem;
    path {
      stroke-width: 3px;
    }
  }
}
.share-btn:hover {
  :deep(.link-icon) {
    path {
      stroke: #000;
      fill: #000;
    }
  }
}
.link-icon {
  margin-left: 0.5rem;
}
.share-container {
  .header-left {
    display: flex;
    justify-content: space-between;
  }
  .form {
    display: flex;
  }

  .tooltip {
    > :deep(.popper) {
      max-width: 30rem;
    }
  }
}

.unfollow-modal--text {
  h4 {
    color: colors.color(grey, secondary);
    margin-top: 1rem;
  }
}
</style>
